import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { IS_REACT_NATIVE_APP } from '@config/environment';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { IApplicationDownloadToastData } from '@datatype/iyagigo/type';
import {
  IYAGIGO_APPLICATION_DOWNLOAD_DISMISS_KEY,
  IYAGIGO_APPLICATION_DOWNLOAD_CLOSE_DISMISS_KEY,
  IYAGIGO_NOTICE_DISMISS_KEY,
  IYAGIGO_NOTICE_CLOSE_DISMISS_KEY,
} from '@constant/iyagigo/variable';

// ###################################
// Dismiss helpers (공용)
// - 일 단위(YYYY-MM-DD) 또는 시간 단위(epoch ms) 저장 → 만료 시 자동 해제
// ###################################
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const checkIsDismissed = (key: string): boolean => {
  const dismissedUntil = localStorage.getItem(key);
  if (!dismissedUntil) return false;
  // epoch ms (시간 단위 dismiss)
  if (/^\d+$/.test(dismissedUntil)) {
    return Date.now() <= Number(dismissedUntil);
  }
  // YYYY-MM-DD (일 단위 dismiss)
  return formatLocalDate(new Date()) <= dismissedUntil;
};

const dismissByDay = (key: string, days: number): void => {
  const until = new Date();
  until.setDate(until.getDate() + days - 1);
  localStorage.setItem(key, formatLocalDate(until));
};

const dismissByHour = (key: string, hours: number): void => {
  const until = Date.now() + hours * 60 * 60 * 1000;
  localStorage.setItem(key, String(until));
};

// ###################################
// Application Download Toast
// ###################################
export const dismissApplicationDownload = (): void => {
  dismissByDay(IYAGIGO_APPLICATION_DOWNLOAD_DISMISS_KEY, 1);
};

// X 닫기 시 단기 dismiss (1시간) — 하루 dismiss와 별도 키 사용
export const dismissApplicationDownloadByHour = (): void => {
  dismissByHour(IYAGIGO_APPLICATION_DOWNLOAD_CLOSE_DISMISS_KEY, 1);
};

export const useApplicationDownloadToast = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const isHomePage = [
    IYAGIGO_PATH.HOME_BEGIN_PATH,
    IYAGIGO_PATH.CLOZE_HOME_PATH,
    IYAGIGO_PATH.CLASSROOM_HOME_PATH,
    IYAGIGO_PATH.TEST_HOME_PATH,
    IYAGIGO_PATH.HUB_HOME_PATH,
    IYAGIGO_PATH.STUDIO_HOME_PATH,
  ].includes(location.pathname);

  useEffect(() => {
    if (
      !IS_REACT_NATIVE_APP &&
      isHomePage &&
      !checkIsDismissed(IYAGIGO_APPLICATION_DOWNLOAD_DISMISS_KEY) &&
      !checkIsDismissed(IYAGIGO_APPLICATION_DOWNLOAD_CLOSE_DISMISS_KEY)
    ) {
      // root-app의 modalActions.initialize() 이후에 실행되도록 지연
      const timer = setTimeout(() => {
        const toastData: IApplicationDownloadToastData = { type: 'application-download', data: null };
        dispatch(modalActions.showNotificationToast(toastData));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);
};

// ###################################
// Notice Modal
// - 공지사항 모달의 자동 노출 트리거 (출시 공지, 이벤트, 서버 점검 등)
// - 공지마다 별도의 localStorage dismiss 키 + 노출 조건 추가
// ###################################
export const dismissNoticeByDay = (): void => {
  dismissByDay(IYAGIGO_NOTICE_DISMISS_KEY, 7);
};

// X 닫기 시 단기 dismiss (1시간) — 일주일 dismiss와 별도 키 사용
export const dismissNoticeByHour = (): void => {
  dismissByHour(IYAGIGO_NOTICE_CLOSE_DISMISS_KEY, 1);
};

export const useNoticeModal = () => {
  const dispatch = useTypedDispatch();
  const location = useLocation();
  const isHomePage = [
    IYAGIGO_PATH.HOME_BEGIN_PATH,
    IYAGIGO_PATH.CLOZE_HOME_PATH,
    IYAGIGO_PATH.CLASSROOM_HOME_PATH,
    IYAGIGO_PATH.TEST_HOME_PATH,
    IYAGIGO_PATH.HUB_HOME_PATH,
    IYAGIGO_PATH.STUDIO_HOME_PATH,
  ].includes(location.pathname);

  useEffect(() => {
    // [폐기] AI 말하기 출시 안내 — 버전과 무관하게 노출돼 최신 앱 사용자에게도 떴다.
    // 앱 버전 체크(useApplicationVersionCheck)의 ApplicationUpdateModal로 대체. 되돌릴 때를 위해 보존.
    // if (
    //   location.pathname === IYAGIGO_PATH.SPEAKING_HOME_PATH &&
    //   !checkIsDismissed(IYAGIGO_NOTICE_DISMISS_KEY) &&
    //   !checkIsDismissed(IYAGIGO_NOTICE_CLOSE_DISMISS_KEY)
    // ) {
    //   // root-app의 modalActions.initialize() 이후에 실행되도록 지연
    //   const timer = setTimeout(() => {
    //     dispatch(modalActions.showNoticeModal({ type: 'speaking-app-notice' }));
    //   }, 500);
    //   return () => clearTimeout(timer);
    // }

    // 향후 이벤트/서버 점검 등 추가 공지는 여기에 우선순위에 맞춰 추가
  }, [location.pathname]);
};
